import React, { useEffect, useState } from 'react';
import { CheckCircle2, Crown, ShieldCheck, Zap, ArrowRight, Loader2, XCircle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [email, setEmail] = useState('');
  const [packageType, setPackageType] = useState('PREMIUM');

  useEffect(() => {
    const processPayment = async () => {
      try {
        const urlPaymentId = searchParams.get('payment_id');
        const urlStatus = searchParams.get('status');
        const urlEmail = searchParams.get('email');
        setPaymentId(urlPaymentId);
        setEmail(urlEmail);

        if (urlStatus !== 'succeeded') {
          throw new Error(t('payment_success.error.status_failed') + urlStatus);
        }

        const savedFormData = sessionStorage.getItem('dodo_form_data');
        if (!savedFormData) {
          throw new Error('Podaci forme nisu pronađeni. Kontaktirajte podršku s Payment ID-em.');
        }

        const formData = JSON.parse(savedFormData);
        const pkg = formData.package || 'PREMIUM';
        setPackageType(pkg);

        // ── Biramo pravu edge funkciju i iznos zavisno od paketa ────────────
        let edgeFunction = 'premium-registration';
        let paymentAmount = 230.00;

        if (pkg === 'BASIC') {
          edgeFunction = 'basic-registration';
          paymentAmount = 75.00;
        } else if (pkg === 'BASIC_MONTHLY') {
          // Basic Monthly (TEST MODE) — odvojena edge funkcija, 1 mjesec trajanja.
          edgeFunction = 'basic-registration-monthly';
          paymentAmount = 7.99;
        } else if (pkg === 'PREMIUM_MONTHLY') {
          // Premium Monthly (TEST MODE) — odvojena edge funkcija, 1 mjesec trajanja.
          edgeFunction = 'premium-registration-monthly';
          paymentAmount = 44.99;
        } else if (pkg === 'ENTERPRISE') {
          edgeFunction = 'enterprise-registration';
          paymentAmount = 615.00;
        } else if (pkg === 'ENTERPRISE_MONTHLY') {
          // NOVO: Enterprise Monthly (TEST MODE) — koristi ODVOJENU edge
          // funkciju enterprise-registration-monthly (mjesečni ciklus, 1
          // mjesec trajanja), ne dijeli logiku sa Annual enterprise-registration.
          edgeFunction = 'enterprise-registration-monthly';
          paymentAmount = 119.99;
        }

        const { error } = await supabase.functions.invoke(edgeFunction, {
          body: {
            name: formData.name,
            surname: formData.surname,
            company: formData.company,
            email: formData.email,
            phone: formData.phone,
            package: pkg,
            payment_id: urlPaymentId,
            payment_status: 'COMPLETED',
            payment_amount: paymentAmount,
          },
        });

        if (error) throw error;

        sessionStorage.removeItem('dodo_form_data');
        setStatus('success');
      } catch (err) {
        console.error('PaymentSuccess greška:', err);
        setErrorMsg(err.message || 'Nepoznata greška.');
        setStatus('error');
      }
    };

    processPayment();
  }, [searchParams, t]);

  // ── Namespace i ikona zavisno od paketa ───────────────────────────────────
  // *_MONTHLY varijante koriste ISTI namespace kao njihov Annual parnjak —
  // razlikuju se samo cijena/edge funkcija u logici iznad i receiptPackageKey
  // ispod (jer se prevodi za trajanje NE dupliraju po billing cycle-u,
  // već je isti "success" ekran za oba slučaja istog paketa).
  const isBasicVariant = packageType === 'BASIC' || packageType === 'BASIC_MONTHLY';
  const isPremiumVariant = packageType === 'PREMIUM' || packageType === 'PREMIUM_MONTHLY';
  const isEnterpriseVariant = packageType === 'ENTERPRISE' || packageType === 'ENTERPRISE_MONTHLY';

  const ns = isBasicVariant
    ? 'basic_payment_success'
    : isEnterpriseVariant
      ? 'enterprise_payment_success'
      : 'payment_success';

  // ── Ključ za trajanje paketa na receiptu ──────────────────────────────────

// za mjesečne varijante — 'monthly'.
const isMonthlyVariant = packageType === 'BASIC_MONTHLY' || packageType === 'PREMIUM_MONTHLY' || packageType === 'ENTERPRISE_MONTHLY';
const receiptPackageKey = isMonthlyVariant
  ? 'receipt_package_value_monthly'
  : 'receipt_package_value';
  const BadgeIcon = isBasicVariant ? ShieldCheck : isEnterpriseVariant ? Zap : Crown;
  const badgeColor = isBasicVariant
    ? 'text-primary'
    : isEnterpriseVariant
      ? 'text-slate-700 dark:text-slate-300'
      : 'text-amber-600';

  // ── LOADING ────────────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-6">
          <div className="relative w-20 h-20 mx-auto">
            <Loader2 className="w-20 h-20 text-primary animate-spin" />
            <BadgeIcon className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-primary" size={24} />
          </div>
          <p className="text-sm font-black uppercase tracking-widest text-primary animate-pulse italic">
            {t(`${ns}.loading`)}
          </p>
        </div>
      </div>
    );
  }

  // ── ERROR ─────────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-card border-2 border-red-500/20 p-8 md:p-12 rounded-[2.5rem] text-center shadow-2xl">
            <div className="bg-red-500 text-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-red-500/20">
              <XCircle size={40} />
            </div>
            <h1 className="text-2xl font-black uppercase tracking-tighter mb-3">{t(`${ns}.error_title`)}</h1>
            <p className="text-muted-foreground text-sm font-medium mb-4">{t(`${ns}.error_subtitle`)}</p>
            {paymentId && (
              <div className="bg-background/80 border border-border rounded-2xl p-4 mb-6 text-left space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-muted-foreground font-black uppercase">{t(`${ns}.error_label_id`)}</span>
                  <span className="text-[10px] font-mono font-bold text-primary">{paymentId}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-muted-foreground font-black uppercase">{t(`${ns}.error_label_msg`)}</span>
                  <span className="text-[10px] font-bold text-red-500 max-w-[200px] text-right">{errorMsg}</span>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground mb-8">
              {t(`${ns}.error_contact`)} <span className="text-primary font-bold">info@orizon-aix.com</span>
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl shadow-primary/30 hover:scale-105 transition-all active:scale-95"
            >
              {t(`${ns}.btn_home`)} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── SUCCESS ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-card border-2 border-emerald-500/20 p-8 md:p-12 rounded-[2.5rem] text-center shadow-2xl">
          <div className="bg-emerald-500 text-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20">
            <CheckCircle2 size={40} />
          </div>
          <div className={`flex items-center justify-center gap-2 mb-3 ${badgeColor}`}>
            {isBasicVariant && <ShieldCheck size={16} />}
            {isEnterpriseVariant && <Zap fill="currentColor" size={16} />}
            {isPremiumVariant && <Crown fill="currentColor" size={16} />}
            <span className="font-black uppercase tracking-widest text-xs">{t(`${ns}.badge`)}</span>
          </div>
          <h1 className="text-3xl font-black uppercase tracking-tighter mb-2">{t(`${ns}.title`)}</h1>
          <p className="text-muted-foreground text-sm font-medium mb-8">
            {t(`${ns}.subtitle`)} <span className="text-primary font-bold">{email}</span> {t(`${ns}.subtitle2`)}
          </p>

          <div className="bg-background/80 border border-border rounded-3xl p-6 text-left space-y-3 mb-8 shadow-inner relative">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-4 text-[9px] font-black uppercase tracking-[0.2em] text-muted-foreground border border-border rounded-full whitespace-nowrap">
              {t(`${ns}.receipt_title`)}
            </div>
            <div className="flex justify-between items-center pt-2">
              <span className="text-[10px] text-muted-foreground font-black uppercase">{t(`${ns}.receipt_payment_id`)}</span>
              <span className="text-[10px] font-mono font-bold text-primary truncate max-w-[160px]">{paymentId}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-muted-foreground font-black uppercase">{t(`${ns}.receipt_package`)}</span>
              {/* "30 dana" za *_MONTHLY, "365 dana" za annual varijante */}
              <span className="text-[10px] font-black uppercase">{t(`${ns}.${receiptPackageKey}`)}</span>
            </div>
            <div className="pt-3 border-t border-dashed border-border flex justify-between items-center">
              <span className="text-[10px] font-black uppercase text-muted-foreground">{t(`${ns}.receipt_status`)}</span>
              <span className="bg-emerald-500 text-white text-[10px] font-black px-4 py-1 rounded-full uppercase">
                {t(`${ns}.receipt_status_value`)}
              </span>
            </div>
          </div>

          <Link
            to="/"
            className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl shadow-primary/30 hover:scale-105 transition-all active:scale-95"
          >
            {t(`${ns}.btn_home`)} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;